import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid2 as Grid,
  Tab,
  Tabs,
  Alert,
  Tooltip,
  CircularProgress,
  Snackbar,
  LinearProgress,
  InputAdornment,
} from '@mui/material';
import {
  CloudUpload,
  Download,
  Visibility,
  BlockOutlined,
  MarkEmailRead,
  PersonAdd,
  Refresh,
  Search,
  DeleteOutline,
  AttachFile,
  FilePresent,
  CheckCircle,
  Cancel,
  HourglassEmpty,
} from '@mui/icons-material';
import api from '../../services/api';

// ─── Types ───────────────────────────────────────────────────────────────────

interface DigitalProduct {
  ID: string;
  pg_produto_ID: string;
  FICHEIRO_URL: string;
  NOME_FICHEIRO: string | null;
  FORMATO: string | null;
  TAMANHO_MB: number | null;
  MAX_DOWNLOADS: number | null;
  VALIDADE_DIAS: number | null;
  VERSAO: string;
  DATA_REGISTO: string;
  PRODUTO_NOME: string;
  TIPO: string;
}

interface DigitalAccess {
  ID: string;
  TOKEN_ACESSO: string;
  EMAIL_CLIENTE: string;
  NOME_CLIENTE: string | null;
  DOWNLOADS_USADOS: number;
  DATA_EXPIRACAO: string | null;
  ESTADO: 'A' | 'P' | 'I';
  DATA_REGISTO: string;
  pg_produto_ID: string;
  pg_order_loja_ID: string | null;
  PRODUTO_NOME: string;
}

interface ShopProduct {
  id: string;
  name: string;
  tipo?: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function estadoChip(estado: string) {
  if (estado === 'A') return <Chip label="Ativo" color="success" size="small" icon={<CheckCircle />} />;
  if (estado === 'P') return <Chip label="Pendente" color="warning" size="small" icon={<HourglassEmpty />} />;
  return <Chip label="Revogado" color="default" size="small" icon={<Cancel />} />;
}

function formatoChip(formato: string | null) {
  if (!formato) return null;
  const color = formato === 'PDF' ? 'error' : formato === 'MP4' || formato === 'MOV' ? 'secondary' : 'default';
  return <Chip label={formato} color={color as any} size="small" sx={{ fontWeight: 700, fontSize: 11 }} />;
}

// ─── Component ───────────────────────────────────────────────────────────────

interface TabPanelProps { children?: React.ReactNode; index: number; value: number; }
function TabPanel({ children, value, index }: TabPanelProps) {
  return <div role="tabpanel" hidden={value !== index}>{value === index && <Box sx={{ pt: 2 }}>{children}</Box>}</div>;
}

const DigitalProducts: React.FC = () => {
  const [tab, setTab] = useState(0);

  // ── Products tab ──────────────────────────────────────────────────────────
  const [shopProducts, setShopProducts] = useState<ShopProduct[]>([]);
  const [digitalProducts, setDigitalProducts] = useState<Record<string, DigitalProduct | null>>({});
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [productPage, setProductPage] = useState(0);
  const [productRowsPerPage] = useState(20);
  const [productSearch, setProductSearch] = useState('');

  // Upload dialog
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadProduct, setUploadProduct] = useState<ShopProduct | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(false);
  const [maxDownloads, setMaxDownloads] = useState('');
  const [validadeDias, setValidadeDias] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Detach confirm
  const [detachId, setDetachId] = useState<string | null>(null);
  const [detachLoading, setDetachLoading] = useState(false);

  // ── Accesses tab ──────────────────────────────────────────────────────────
  const [accesses, setAccesses] = useState<DigitalAccess[]>([]);
  const [accessTotal, setAccessTotal] = useState(0);
  const [accessPage, setAccessPage] = useState(0);
  const [accessRowsPerPage] = useState(20);
  const [accessEstado, setAccessEstado] = useState('');
  const [loadingAccesses, setLoadingAccesses] = useState(false);

  // Grant dialog
  const [grantOpen, setGrantOpen] = useState(false);
  const [grantProdutoId, setGrantProdutoId] = useState('');
  const [grantEmail, setGrantEmail] = useState('');
  const [grantNome, setGrantNome] = useState('');
  const [grantValidadeDias, setGrantValidadeDias] = useState('');
  const [grantLoading, setGrantLoading] = useState(false);

  // Snackbar
  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  const showSnack = (message: string, severity: 'success' | 'error' = 'success') =>
    setSnack({ open: true, message, severity });

  // ── Load products ─────────────────────────────────────────────────────────

  const loadProducts = async () => {
    setLoadingProducts(true);
    try {
      // Um único request que devolve todos os produtos da entidade + metadados digitais
      const res = await api.get('/portal/digital/products');
      const rows: any[] = res.data?.data || [];

      const products: ShopProduct[] = rows.map((r: any) => ({
        id:   r.produtoId,
        name: r.produtoNome,
        tipo: r.tipo,
      }));
      setShopProducts(products);

      const digitalMap: Record<string, DigitalProduct | null> = {};
      for (const r of rows) {
        digitalMap[r.produtoId] = r.digital ?? null;
      }
      setDigitalProducts(digitalMap);
    } catch (err: any) {
      showSnack(err.response?.data?.error || err.message, 'error');
    } finally {
      setLoadingProducts(false);
    }
  };

  // ── Load accesses ─────────────────────────────────────────────────────────

  const loadAccesses = async () => {
    setLoadingAccesses(true);
    try {
      const params: any = { page: accessPage + 1, limit: accessRowsPerPage };
      if (accessEstado) params.estado = accessEstado;
      const res = await api.get('/portal/digital/accesses', { params });
      setAccesses(res.data?.data || []);
      setAccessTotal(res.data?.pagination?.total || 0);
    } catch (err: any) {
      showSnack(err.response?.data?.error || err.message, 'error');
    } finally {
      setLoadingAccesses(false);
    }
  };

  useEffect(() => { loadProducts(); }, []);
  useEffect(() => { if (tab === 1) loadAccesses(); }, [tab, accessPage, accessEstado]);

  // ── Upload flow ───────────────────────────────────────────────────────────

  const openUpload = (product: ShopProduct) => {
    setUploadProduct(product);
    setUploadFile(null);
    setMaxDownloads('');
    setValidadeDias('');
    setUploadDialogOpen(true);
  };

  const handleUpload = async () => {
    if (!uploadProduct || !uploadFile) return;
    setUploadProgress(true);
    try {
      // 1. Upload file to storage
      const formData = new FormData();
      formData.append('file', uploadFile);
      const upRes = await api.post(`/portal/digital/products/${uploadProduct.id}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { ficheiroUrl, formato, tamanhoMb, nomeFicheiro, storagePath } = upRes.data.data;

      // 2. Attach metadata to product
      await api.post(`/portal/digital/products/${uploadProduct.id}/attach`, {
        ficheiroUrl,
        storagePath,
        nomeFicheiro,
        formato,
        tamanhoMb,
        maxDownloads: maxDownloads ? parseInt(maxDownloads) : undefined,
        validadeDias: validadeDias ? parseInt(validadeDias) : undefined,
      });

      showSnack('Ficheiro digital associado ao produto com sucesso');
      setUploadDialogOpen(false);
      loadProducts();
    } catch (err: any) {
      showSnack(err.response?.data?.error || err.message, 'error');
    } finally {
      setUploadProgress(false);
    }
  };

  // ── Detach ────────────────────────────────────────────────────────────────

  const handleDetach = async () => {
    if (!detachId) return;
    setDetachLoading(true);
    try {
      await api.delete(`/portal/digital/products/${detachId}/detach`);
      showSnack('Ficheiro digital removido');
      setDetachId(null);
      loadProducts();
    } catch (err: any) {
      showSnack(err.response?.data?.error || err.message, 'error');
    } finally {
      setDetachLoading(false);
    }
  };

  // ── Revoke / Resend ───────────────────────────────────────────────────────

  const handleRevoke = async (id: string) => {
    try {
      await api.patch(`/portal/digital/access/${id}/revoke`);
      showSnack('Acesso revogado');
      loadAccesses();
    } catch (err: any) {
      showSnack(err.response?.data?.error || err.message, 'error');
    }
  };

  const handleResend = async (id: string) => {
    try {
      await api.post(`/portal/digital/access/${id}/resend-email`);
      showSnack('Email reenviado com sucesso');
    } catch (err: any) {
      showSnack(err.response?.data?.error || err.message, 'error');
    }
  };

  // ── Grant ─────────────────────────────────────────────────────────────────

  const handleGrant = async () => {
    if (!grantProdutoId || !grantEmail) return;
    setGrantLoading(true);
    try {
      await api.post('/portal/digital/grant', {
        produtoId: grantProdutoId,
        emailCliente: grantEmail,
        nomeCliente: grantNome || undefined,
        validadeDias: grantValidadeDias ? parseInt(grantValidadeDias) : undefined,
      });
      showSnack('Acesso concedido e email enviado');
      setGrantOpen(false);
      setGrantProdutoId(''); setGrantEmail(''); setGrantNome(''); setGrantValidadeDias('');
      if (tab === 1) loadAccesses();
    } catch (err: any) {
      showSnack(err.response?.data?.error || err.message, 'error');
    } finally {
      setGrantLoading(false);
    }
  };

  // ── Filtered products ─────────────────────────────────────────────────────

  const filteredProducts = shopProducts.filter(p =>
    !productSearch || p.name.toLowerCase().includes(productSearch.toLowerCase())
  );
  const paginatedProducts = filteredProducts.slice(
    productPage * productRowsPerPage,
    productPage * productRowsPerPage + productRowsPerPage
  );

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Produtos Digitais</Typography>
          <Typography variant="body2" color="text.secondary">
            Gere ficheiros digitais (PDF, ZIP, vídeos) e controla os acessos dos compradores
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<PersonAdd />}
            onClick={() => setGrantOpen(true)}
          >
            Conceder Acesso
          </Button>
          <IconButton onClick={() => tab === 0 ? loadProducts() : loadAccesses()}>
            <Refresh />
          </IconButton>
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2 }}>
          <Tab label="Produtos" />
          <Tab label={`Acessos${accessTotal > 0 ? ` (${accessTotal})` : ''}`} />
        </Tabs>
      </Paper>

      {/* ── Tab 0: Products ─────────────────────────────────────────────── */}
      <TabPanel value={tab} index={0}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <TextField
            size="small"
            placeholder="Pesquisar produto..."
            value={productSearch}
            onChange={e => { setProductSearch(e.target.value); setProductPage(0); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            sx={{ width: 300 }}
          />
        </Paper>

        {loadingProducts ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Produto</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Ficheiro</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Formato</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Tamanho</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Max. Downloads</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Validade (dias)</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedProducts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        Nenhum produto encontrado
                      </TableCell>
                    </TableRow>
                  ) : paginatedProducts.map(product => {
                    const digital = digitalProducts[product.id];
                    return (
                      <TableRow key={product.id} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>{product.name}</Typography>
                            {product.tipo === 'DIGITAL' && (
                              <Chip label="Digital" size="small" color="secondary" sx={{ mt: 0.5, fontSize: 10 }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {digital ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <FilePresent fontSize="small" color="action" />
                              <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                                {digital.NOME_FICHEIRO || '—'}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.disabled">Sem ficheiro</Typography>
                          )}
                        </TableCell>
                        <TableCell>{formatoChip(digital?.FORMATO ?? null)}</TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {digital?.TAMANHO_MB != null ? `${digital.TAMANHO_MB} MB` : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{digital?.MAX_DOWNLOADS ?? '∞'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{digital?.VALIDADE_DIAS ?? '∞'}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                            <Tooltip title={digital ? 'Substituir ficheiro' : 'Associar ficheiro'}>
                              <IconButton size="small" color="primary" onClick={() => openUpload(product)}>
                                <CloudUpload fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {digital && (
                              <Tooltip title="Remover ficheiro digital">
                                <IconButton size="small" color="error" onClick={() => setDetachId(product.id)}>
                                  <DeleteOutline fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredProducts.length}
              page={productPage}
              onPageChange={(_, p) => setProductPage(p)}
              rowsPerPage={productRowsPerPage}
              rowsPerPageOptions={[20]}
              labelRowsPerPage="Por página:"
            />
          </Paper>
        )}
      </TabPanel>

      {/* ── Tab 1: Accesses ─────────────────────────────────────────────── */}
      <TabPanel value={tab} index={1}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Estado</InputLabel>
              <Select
                value={accessEstado}
                label="Estado"
                onChange={e => { setAccessEstado(e.target.value); setAccessPage(0); }}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="A">Ativos</MenuItem>
                <MenuItem value="P">Pendentes</MenuItem>
                <MenuItem value="I">Revogados</MenuItem>
              </Select>
            </FormControl>
            <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={loadAccesses}>
              Atualizar
            </Button>
          </Box>
        </Paper>

        {loadingAccesses ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Cliente</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Produto</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Estado</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Downloads</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Expira em</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Data Compra</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {accesses.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        Nenhum acesso encontrado
                      </TableCell>
                    </TableRow>
                  ) : accesses.map(a => (
                    <TableRow key={a.ID} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{a.NOME_CLIENTE || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">{a.EMAIL_CLIENTE}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{a.PRODUTO_NOME}</Typography>
                      </TableCell>
                      <TableCell>{estadoChip(a.ESTADO)}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{a.DOWNLOADS_USADOS}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {a.DATA_EXPIRACAO
                            ? new Date(a.DATA_EXPIRACAO).toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' })
                            : '∞'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(a.DATA_REGISTO).toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' })}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          {a.ESTADO === 'A' && (
                            <Tooltip title="Reenviar email de acesso">
                              <IconButton size="small" color="primary" onClick={() => handleResend(a.ID)}>
                                <MarkEmailRead fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {a.ESTADO !== 'I' && (
                            <Tooltip title="Revogar acesso">
                              <IconButton size="small" color="error" onClick={() => handleRevoke(a.ID)}>
                                <BlockOutlined fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={accessTotal}
              page={accessPage}
              onPageChange={(_, p) => setAccessPage(p)}
              rowsPerPage={accessRowsPerPage}
              rowsPerPageOptions={[20]}
              labelRowsPerPage="Por página:"
            />
          </Paper>
        )}
      </TabPanel>

      {/* ── Upload Dialog ────────────────────────────────────────────────── */}
      <Dialog open={uploadDialogOpen} onClose={() => !uploadProgress && setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {digitalProducts[uploadProduct?.id ?? ''] ? 'Substituir' : 'Associar'} Ficheiro Digital
          {uploadProduct && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {uploadProduct.name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {uploadProgress && <LinearProgress sx={{ mb: 2 }} />}
          <Box sx={{ mt: 1 }}>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              accept=".pdf,.zip,.mp4,.mov,.avi,.epub"
              onChange={e => setUploadFile(e.target.files?.[0] || null)}
            />
            <Button
              variant="outlined"
              fullWidth
              startIcon={<AttachFile />}
              onClick={() => fileInputRef.current?.click()}
              sx={{ mb: 1.5, py: 1.5, borderStyle: 'dashed' }}
            >
              {uploadFile ? uploadFile.name : 'Selecionar ficheiro (PDF, ZIP, MP4, EPUB — máx. 200 MB)'}
            </Button>
            {uploadFile && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {uploadFile.name} — {(uploadFile.size / 1_048_576).toFixed(1)} MB
              </Alert>
            )}
            <Grid container spacing={2}>
              <Grid size={6}>
                <TextField
                  label="Máx. downloads (opcional)"
                  type="number"
                  fullWidth size="small"
                  value={maxDownloads}
                  onChange={e => setMaxDownloads(e.target.value)}
                  placeholder="Ilimitado"
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="Validade (dias, opcional)"
                  type="number"
                  fullWidth size="small"
                  value={validadeDias}
                  onChange={e => setValidadeDias(e.target.value)}
                  placeholder="Sem expiração"
                  inputProps={{ min: 1 }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)} disabled={uploadProgress}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!uploadFile || uploadProgress}
            startIcon={uploadProgress ? <CircularProgress size={16} /> : <CloudUpload />}
          >
            {uploadProgress ? 'A enviar...' : 'Enviar e Associar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Detach Confirm Dialog ────────────────────────────────────────── */}
      <Dialog open={!!detachId} onClose={() => setDetachId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remover ficheiro digital?</DialogTitle>
        <DialogContent>
          <Typography>
            O ficheiro digital será desassociado deste produto. Os acessos já concedidos continuarão válidos.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetachId(null)}>Cancelar</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDetach}
            disabled={detachLoading}
            startIcon={detachLoading ? <CircularProgress size={16} /> : <DeleteOutline />}
          >
            Remover
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Grant Access Dialog ──────────────────────────────────────────── */}
      <Dialog open={grantOpen} onClose={() => !grantLoading && setGrantOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Conceder Acesso Manual</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Concede acesso a um produto digital a um cliente (ex: após pagamento por transferência bancária).
            Um email com o link de acesso será enviado automaticamente.
          </Typography>
          <Grid container spacing={2}>
            <Grid size={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Produto Digital *</InputLabel>
                <Select
                  value={grantProdutoId}
                  label="Produto Digital *"
                  onChange={e => setGrantProdutoId(e.target.value)}
                >
                  {shopProducts
                    .filter(p => digitalProducts[p.id] != null)
                    .map(p => (
                      <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Grid>
            <Grid size={12}>
              <TextField
                label="Email do cliente *"
                fullWidth size="small"
                type="email"
                value={grantEmail}
                onChange={e => setGrantEmail(e.target.value)}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                label="Nome do cliente (opcional)"
                fullWidth size="small"
                value={grantNome}
                onChange={e => setGrantNome(e.target.value)}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                label="Validade (dias, opcional)"
                type="number"
                fullWidth size="small"
                value={grantValidadeDias}
                onChange={e => setGrantValidadeDias(e.target.value)}
                placeholder="Sem expiração"
                inputProps={{ min: 1 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGrantOpen(false)} disabled={grantLoading}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleGrant}
            disabled={!grantProdutoId || !grantEmail || grantLoading}
            startIcon={grantLoading ? <CircularProgress size={16} /> : <PersonAdd />}
          >
            {grantLoading ? 'A conceder...' : 'Conceder Acesso'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ─────────────────────────────────────────────────────── */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DigitalProducts;
